import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import {
  fetchLuckyWheelPlayConfig,
  postLuckyWheelSpin,
  type LuckyWheelMode,
  type LuckyWheelPaymentType,
  type LuckyWheelSegment,
  type LuckyWheelTicket,
} from '../../services/luckyWheelService'
import type { PointsOutletContext } from './ModernPointsLayout'
import { fireLuckyWheelSwal } from './luckyWheelSwal'
import './luckyWheelGame.css'

const DEFAULT_SPIN_MS = 8000
const SOUND_STORAGE_KEY = 'htd-lucky-wheel-sound'

function playTone(
  ctx: AudioContext,
  frequency: number,
  duration: number,
  volume: number,
  delay = 0,
  type: OscillatorType = 'sine',
) {
  const start = ctx.currentTime + delay
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()
  oscillator.type = type
  oscillator.frequency.setValueAtTime(frequency, start)
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(volume, start + 0.008)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
  oscillator.connect(gain)
  gain.connect(ctx.destination)
  oscillator.start(start)
  oscillator.stop(start + duration + 0.02)
}

function segmentDisplayLabel(seg: LuckyWheelSegment) {
  if (seg.kind === 'empty') return seg.label || 'โชคไม่เข้า'
  return seg.label || String(seg.amount)
}

/** สเกลรางวัลบนวงล้อให้สัมพันธ์กับราคาโหมดที่เลือก (ตารางตั้งค่าอิงโหมดเบา) */
function scaleSegmentsForMode(
  segments: LuckyWheelSegment[],
  cost: number,
  baseCost: number,
  paymentType: LuckyWheelPaymentType,
): LuckyWheelSegment[] {
  const base = Number(baseCost)
  const c = Number(cost)
  if (!(base > 0) || !(c > 0) || Math.abs(c - base) < 1e-9) {
    return segments.map((s) => ({ ...s }))
  }
  const factor = c / base
  return segments.map((s) => {
    if (s.kind !== 'prize' || !(Number(s.amount) > 0)) return { ...s }
    const raw = Number(s.amount) * factor
    const amount = paymentType === 'cash'
      ? Math.round(raw * 100) / 100
      : Math.max(1, Math.round(raw))
    const label = paymentType === 'cash'
      ? `${Number.isInteger(amount) ? amount : amount.toLocaleString('th-TH', { maximumFractionDigits: 2 })} ฿`
      : `${amount} pts`
    const prev = String(s.label || '')
    const shouldReplaceLabel = !prev || /โชคไม่เข้า/i.test(prev)
      || (/\d/.test(prev) && (paymentType === 'cash' ? /(฿|บาท)/i.test(prev) : /pts|คะแนน|แต้ม/i.test(prev)))
    return { ...s, amount, label: shouldReplaceLabel ? label : prev }
  })
}

/** ยิ่งหมุนนาน ยิ่งหลายรอบ — ให้ความเร็วช่วงต้นใกล้เคียงกันทุกค่า duration */
function turnsForDuration(ms: number) {
  return Math.max(6, Math.round(ms / 900))
}

/**
 * Pointer at top; segment 0 starts at top going clockwise.
 * เพิ่ม jitter ภายในชิ้น (ไม่หยุดกลางชิ้นเป๊ะทุกครั้ง) — ผลยังตรง segmentIndex จาก server
 */
function rotationForSegment(
  segmentIndex: number,
  count: number,
  baseRotation: number,
  extraTurns: number,
) {
  const n = Math.max(1, count)
  const slice = 360 / n
  const jitter = (Math.random() - 0.5) * slice * 0.6
  const centerFromTop = segmentIndex * slice + slice / 2 + jitter
  const target = extraTurns * 360 + (360 - centerFromTop)
  // Keep absolute rotation increasing so CSS transition always spins forward
  const normalizedBase = ((baseRotation % 360) + 360) % 360
  let next = baseRotation - normalizedBase + target
  if (next <= baseRotation) next += 360
  return next
}

function polarPoint(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)]
}

/** ปรับความสว่างของสี hex (percent บวก = สว่างขึ้น) */
function shadeColor(hex: string, percent: number) {
  const m = /^#?([a-f\d]{6})$/i.exec(hex || '')
  if (!m) return hex || '#dc2626'
  const num = parseInt(m[1], 16)
  const amt = Math.round(2.55 * percent)
  const r = Math.min(255, Math.max(0, (num >> 16) + amt))
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amt))
  const b = Math.min(255, Math.max(0, (num & 0xff) + amt))
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

const WHEEL_C = 110
const WEDGE_R = 90
const RIM_R = 99

/** วงล้อ SVG สไตล์ 3D — ขอบทองเมทัลลิก + ไฟรอบวง + เงา dome + ป้ายรางวัลคมชัด */
function WheelSvg({
  segments,
  winnerIndex,
}: {
  segments: LuckyWheelSegment[]
  winnerIndex: number | null
}) {
  const n = Math.max(1, segments.length)
  const slice = 360 / n
  const fontSize = n <= 6 ? 13 : n <= 9 ? 11 : n <= 12 ? 9.5 : 8.5
  const maxChars = n <= 8 ? 12 : 10
  const lightCount = Math.max(12, n * 2)
  return (
    <svg viewBox="0 0 220 220" className="htd-lw__svg">
      <defs>
        {/* เงา dome — สว่างกลางบน มืดขอบ ให้ดูนูน */}
        <radialGradient id="lwShade" cx="50%" cy="40%" r="68%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.30)" />
          <stop offset="45%" stopColor="rgba(255,255,255,0.07)" />
          <stop offset="82%" stopColor="rgba(0,0,0,0.16)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.42)" />
        </radialGradient>
        {/* ขอบทองเมทัลลิก */}
        <linearGradient id="lwRim" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff3c4" />
          <stop offset="28%" stopColor="#f5c451" />
          <stop offset="55%" stopColor="#b45309" />
          <stop offset="80%" stopColor="#8a4a06" />
          <stop offset="100%" stopColor="#fde68a" />
        </linearGradient>
        {segments.map((seg, i) => {
          const base = seg.color || (seg.kind === 'empty' ? '#64748b' : '#dc2626')
          return (
            <radialGradient key={seg.id || i} id={`lwSeg${i}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={shadeColor(base, 26)} />
              <stop offset="62%" stopColor={base} />
              <stop offset="100%" stopColor={shadeColor(base, -22)} />
            </radialGradient>
          )
        })}
      </defs>

      {/* ฐานขอบ (โลหะเข้ม) */}
      <circle cx={WHEEL_C} cy={WHEEL_C} r={RIM_R + 8} fill="#2b1503" />

      {/* ชิ้นวงล้อ */}
      {segments.map((seg, i) => {
        const start = i * slice
        const end = (i + 1) * slice
        const [x1, y1] = polarPoint(WHEEL_C, WHEEL_C, WEDGE_R, start)
        const [x2, y2] = polarPoint(WHEEL_C, WHEEL_C, WEDGE_R, end)
        const large = slice > 180 ? 1 : 0
        const isWinner = winnerIndex === i
        return (
          <g key={seg.id || i} className={isWinner ? 'htd-lw__wedge is-winner' : 'htd-lw__wedge'}>
            <path
              d={`M ${WHEEL_C} ${WHEEL_C} L ${x1} ${y1} A ${WEDGE_R} ${WEDGE_R} 0 ${large} 1 ${x2} ${y2} Z`}
              fill={`url(#lwSeg${i})`}
              stroke={isWinner ? '#ffe08a' : 'rgba(255,244,200,0.9)'}
              strokeWidth={isWinner ? 3 : 1.2}
            />
          </g>
        )
      })}

      {/* เงา dome ทับทั้งวง ให้ดูนูน 3D */}
      <circle cx={WHEEL_C} cy={WHEEL_C} r={WEDGE_R} fill="url(#lwShade)" pointerEvents="none" />

      {/* ป้ายรางวัล (วาดหลังเงา ให้ตัวหนังสือสดชัด) */}
      {segments.map((seg, i) => {
        const mid = i * slice + slice / 2
        const raw = segmentDisplayLabel(seg)
        const label = raw.length > maxChars ? `${raw.slice(0, maxChars - 1)}…` : raw
        return (
          <g key={`t-${seg.id || i}`} transform={`rotate(${mid - 90} ${WHEEL_C} ${WHEEL_C})`}>
            <text
              x={WHEEL_C + 26}
              y={WHEEL_C}
              textAnchor="start"
              dominantBaseline="middle"
              fontSize={fontSize}
              fontWeight={900}
              fill="#fff"
              letterSpacing="0.4"
              style={{ paintOrder: 'stroke', stroke: 'rgba(30,10,0,0.6)', strokeWidth: 2.6 }}
            >
              {label}
            </text>
          </g>
        )
      })}

      {/* วงแหวนทองด้านใน + ขอบทองหลัก */}
      <circle cx={WHEEL_C} cy={WHEEL_C} r={WEDGE_R + 1} fill="none" stroke="#f5c451" strokeWidth="1.6" />
      <circle cx={WHEEL_C} cy={WHEEL_C} r={RIM_R} fill="none" stroke="url(#lwRim)" strokeWidth="13" />
      <circle cx={WHEEL_C} cy={WHEEL_C} r={RIM_R + 6.5} fill="none" stroke="rgba(0,0,0,0.45)" strokeWidth="1.4" />

      {/* ไฟรอบขอบ */}
      {Array.from({ length: lightCount }).map((_, i) => {
        const [lx, ly] = polarPoint(WHEEL_C, WHEEL_C, RIM_R, (360 / lightCount) * i)
        return (
          <g key={`light-${i}`} className="htd-lw__light" style={{ animationDelay: `${(i % 2) * 0.55}s` }}>
            <circle cx={lx} cy={ly} r={3.4} fill="rgba(255,214,120,0.45)" />
            <circle cx={lx} cy={ly} r={1.9} fill="#fff8dc" />
          </g>
        )
      })}

      {/* เส้นเรืองแสงชิ้นที่ถูกรางวัล — ชั้นบนสุด ไม่โดนเงาทับ */}
      {winnerIndex !== null && winnerIndex >= 0 && winnerIndex < n
        ? (() => {
            const start = winnerIndex * slice
            const end = (winnerIndex + 1) * slice
            const [x1, y1] = polarPoint(WHEEL_C, WHEEL_C, WEDGE_R, start)
            const [x2, y2] = polarPoint(WHEEL_C, WHEEL_C, WEDGE_R, end)
            const large = slice > 180 ? 1 : 0
            return (
              <path
                className="htd-lw__winner-outline"
                d={`M ${WHEEL_C} ${WHEEL_C} L ${x1} ${y1} A ${WEDGE_R} ${WEDGE_R} 0 ${large} 1 ${x2} ${y2} Z`}
                fill="rgba(255,224,138,0.14)"
                stroke="#ffe08a"
                strokeWidth="3"
              />
            )
          })()
        : null}
    </svg>
  )
}

function ticketMatches(
  ticket: LuckyWheelTicket,
  paymentType: LuckyWheelPaymentType,
  modeKey: string,
) {
  const payOk = !ticket.paymentType || ticket.paymentType === 'any' || ticket.paymentType === paymentType
  const modeOk = !ticket.modeKey || ticket.modeKey === modeKey
  const gameOk = !ticket.game || ticket.game === 'any' || ticket.game === 'lucky_wheel'
  return payOk && modeOk && gameOk && (ticket.remaining ?? 0) > 0
}

const LuckyWheelGamePage: React.FC = () => {
  const { refreshUserSummary, pointsBalance, cashBalance } = useOutletContext<PointsOutletContext>()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userEntryVisible, setUserEntryVisible] = useState(false)
  const [modes, setModes] = useState<LuckyWheelMode[]>([])
  const [segmentsPoints, setSegmentsPoints] = useState<LuckyWheelSegment[]>([])
  const [segmentsCash, setSegmentsCash] = useState<LuckyWheelSegment[]>([])
  const [baseCostPoints, setBaseCostPoints] = useState(30)
  const [baseCostCash, setBaseCostCash] = useState(3)
  const [tickets, setTickets] = useState<LuckyWheelTicket[]>([])

  const [paymentType, setPaymentType] = useState<LuckyWheelPaymentType>('points')
  const [modeKey, setModeKey] = useState('')
  const [useTicket, setUseTicket] = useState(false)

  const [rotation, setRotation] = useState(0)
  const [spinning, setSpinning] = useState(false)
  const [resultText, setResultText] = useState('')
  const [spinDurationMs, setSpinDurationMs] = useState(DEFAULT_SPIN_MS)
  const [winnerIndex, setWinnerIndex] = useState<number | null>(null)
  const [soundEnabled, setSoundEnabled] = useState(
    () => window.localStorage.getItem(SOUND_STORAGE_KEY) !== 'off',
  )
  const audioContextRef = useRef<AudioContext | null>(null)
  const tickTimerRef = useRef<number | null>(null)

  const getAudioContext = useCallback(() => {
    const ctx = audioContextRef.current || new AudioContext()
    audioContextRef.current = ctx
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  }, [])

  const stopSpinSound = useCallback(() => {
    if (tickTimerRef.current !== null) {
      window.clearTimeout(tickTimerRef.current)
      tickTimerRef.current = null
    }
  }, [])

  const startSpinSound = useCallback(
    (durationMs: number) => {
      stopSpinSound()
      if (!soundEnabled) return
      const ctx = getAudioContext()
      const startedAt = performance.now()

      const tick = () => {
        const progress = Math.min(1, (performance.now() - startedAt) / durationMs)
        // เสียงเข็มกระทบหมุด: ช่วงแรกถี่ แล้วค่อย ๆ ช้าตามวงล้อ
        playTone(ctx, 1050 - progress * 420, 0.035, 0.075, 0, 'square')
        if (progress < 0.985) {
          const interval = 48 + Math.pow(progress, 2.35) * 330
          tickTimerRef.current = window.setTimeout(tick, interval)
        }
      }
      tick()
    },
    [getAudioContext, soundEnabled, stopSpinSound],
  )

  const playResultSound = useCallback(
    (won: boolean) => {
      if (!soundEnabled) return
      const ctx = getAudioContext()
      if (won) {
        // fanfare สั้น ๆ เมื่อได้รางวัล
        playTone(ctx, 523.25, 0.22, 0.12, 0)
        playTone(ctx, 659.25, 0.22, 0.12, 0.13)
        playTone(ctx, 783.99, 0.24, 0.13, 0.26)
        playTone(ctx, 1046.5, 0.55, 0.15, 0.4)
      } else {
        playTone(ctx, 330, 0.2, 0.08, 0, 'triangle')
        playTone(ctx, 247, 0.38, 0.09, 0.16, 'triangle')
      }
    },
    [getAudioContext, soundEnabled],
  )

  const toggleSound = () => {
    const next = !soundEnabled
    setSoundEnabled(next)
    window.localStorage.setItem(SOUND_STORAGE_KEY, next ? 'on' : 'off')
    if (next) playTone(getAudioContext(), 880, 0.12, 0.06)
    else stopSpinSound()
  }

  useEffect(
    () => () => {
      stopSpinSound()
      void audioContextRef.current?.close()
    },
    [stopSpinSound],
  )

  const loadConfig = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchLuckyWheelPlayConfig()
      const data = res.data
      setUserEntryVisible(data.userEntryVisible === true)
      const nextModes = data.modes || []
      setModes(nextModes)
      setSegmentsPoints(data.segmentsPoints || [])
      setSegmentsCash(data.segmentsCash || [])
      setBaseCostPoints(Number(data.baseCostPoints) > 0 ? Number(data.baseCostPoints) : 30)
      setBaseCostCash(Number(data.baseCostCash) > 0 ? Number(data.baseCostCash) : 3)
      setTickets(data.tickets || [])
      setSpinDurationMs(Number(data.spinDurationMs) || DEFAULT_SPIN_MS)
      setModeKey((prev) => {
        if (prev && nextModes.some((m) => m.key === prev)) return prev
        return nextModes[0]?.key || ''
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'โหลดตั้งค่าไม่สำเร็จ')
      setUserEntryVisible(false)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadConfig()
  }, [loadConfig])

  const selectedMode = useMemo(
    () => modes.find((m) => m.key === modeKey) || modes[0],
    [modes, modeKey],
  )

  const segments = useMemo(() => {
    const raw = paymentType === 'cash' ? segmentsCash : segmentsPoints
    if (!selectedMode) return raw
    const cost = paymentType === 'cash'
      ? Number(selectedMode.costCash)
      : Number(selectedMode.costPoints)
    const base = paymentType === 'cash' ? baseCostCash : baseCostPoints
    return scaleSegmentsForMode(raw, cost, base, paymentType)
  }, [paymentType, segmentsCash, segmentsPoints, selectedMode, baseCostCash, baseCostPoints])

  const matchingTickets = useMemo(() => {
    if (!selectedMode) return []
    return tickets.filter((t) => ticketMatches(t, paymentType, selectedMode.key))
  }, [tickets, paymentType, selectedMode])

  const ticketCount = matchingTickets.reduce((sum, t) => sum + (Number(t.remaining) || 0), 0)

  useEffect(() => {
    if (ticketCount <= 0 && useTicket) setUseTicket(false)
  }, [ticketCount, useTicket])

  const costLabel = useMemo(() => {
    if (!selectedMode) return ''
    if (useTicket) return 'ใช้ตั๋วเล่นฟรี'
    if (paymentType === 'cash') {
      return `${Number(selectedMode.costCash).toLocaleString('th-TH')} บาท`
    }
    return `${Number(selectedMode.costPoints).toLocaleString('th-TH')} คะแนน`
  }, [selectedMode, paymentType, useTicket])

  const onSpin = async () => {
    if (!selectedMode || spinning || segments.length === 0) return

    if (!useTicket) {
      const confirm = await fireLuckyWheelSwal({
        title: 'ยืนยันการหมุน',
        message: `หมุนโหมด「${selectedMode.label}」ด้วย${paymentType === 'cash' ? 'เงินสด' : 'คะแนน'} ${costLabel}?`,
        tone: 'confirm',
        showCancel: true,
        confirmText: 'หมุนเลย',
        cancelText: 'ยกเลิก',
      })
      if (!confirm.isConfirmed) return
    }

    setSpinning(true)
    setResultText('กำลังหมุน... ลุ้นเลย!')
    setError(null)
    setWinnerIndex(null)
    // สร้าง AudioContext ขณะยังอยู่ใน user gesture เพื่อให้ Safari/iOS อนุญาตเสียง
    if (soundEnabled) getAudioContext()

    try {
      const chosenTicket = useTicket ? matchingTickets[0] : undefined
      const res = await postLuckyWheelSpin({
        modeKey: selectedMode.key,
        paymentType,
        ...(useTicket
          ? { useTicket: true, ...(chosenTicket?.ticketId ? { ticketId: chosenTicket.ticketId } : {}) }
          : {}),
      })
      const data = res.data
      const nextRotation = rotationForSegment(
        data.segmentIndex,
        segments.length,
        rotation,
        turnsForDuration(spinDurationMs),
      )
      setRotation(nextRotation)
      startSpinSound(spinDurationMs)

      window.setTimeout(() => {
        stopSpinSound()
        const won =
          data.kind === 'prize' && Number(data.prizeAmount) > 0
            ? `ได้ ${Number(data.prizeAmount).toLocaleString('th-TH')} ${
                data.prizeCurrency === 'cash' ? 'บาท' : 'คะแนน'
              }`
            : data.segmentLabel || 'โชคไม่เข้า'
        setResultText(won)
        setSpinning(false)
        setWinnerIndex(data.segmentIndex)
        playResultSound(data.kind === 'prize' && Number(data.prizeAmount) > 0)
        void refreshUserSummary()
        void loadConfig()
        void fireLuckyWheelSwal({
          title: data.kind === 'prize' ? 'ยินดีด้วย!' : 'โชคไม่เข้า',
          message: won,
          tone: data.kind === 'prize' ? 'success' : 'info',
        })
      }, spinDurationMs)
    } catch (err) {
      stopSpinSound()
      setSpinning(false)
      setResultText('')
      const message = err instanceof Error ? err.message : 'หมุนไม่สำเร็จ'
      setError(message)
      await fireLuckyWheelSwal({ title: 'หมุนไม่สำเร็จ', message, tone: 'error' })
    }
  }

  if (loading) {
    return (
      <div className="htd-lw">
        <p className="htd-lw__muted">
          <i className="fas fa-spinner fa-spin me-2" />
          กำลังโหลดวงล้อมหาโชค...
        </p>
      </div>
    )
  }

  if (!userEntryVisible || modes.length === 0) {
    return (
      <div className="htd-lw">
        <p className="htd-lw__muted">เกมส์วงล้อมหาโชคยังไม่เปิดให้เล่นในขณะนี้</p>
      </div>
    )
  }

  return (
    <div className="htd-lw">
      <div className="htd-lw__head">
        <div>
          <h2 className="htd-lw__title">วงล้อมหาโชค</h2>
          <p className="htd-lw__sub">หมุนวงล้อลุ้นรางวัลคะแนนหรือเงินสด</p>
        </div>
        <Link to="/history/games/lucky-wheel" className="htd-lw__history-link">
          <i className="fas fa-history" />
          ประวัติ
        </Link>
      </div>

      <div className="htd-lw__panel">
        <div className="htd-lw__label">ช่องทางชำระ</div>
        <div className="htd-lw__toggle" role="group" aria-label="ช่องทางชำระ">
          <button
            type="button"
            className={paymentType === 'points' ? 'is-active' : ''}
            disabled={spinning}
            onClick={() => setPaymentType('points')}
          >
            <span className="htd-lw__pay-name">คะแนน</span>
            <span className="htd-lw__pay-bal">
              คงเหลือ {Number(pointsBalance || 0).toLocaleString('th-TH')} pts
            </span>
          </button>
          <button
            type="button"
            className={paymentType === 'cash' ? 'is-active' : ''}
            disabled={spinning}
            onClick={() => setPaymentType('cash')}
          >
            <span className="htd-lw__pay-name">เงินสด</span>
            <span className="htd-lw__pay-bal">
              คงเหลือ {Number(cashBalance || 0).toLocaleString('th-TH', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}{' '}
              ฿
            </span>
          </button>
        </div>
      </div>

      <div className="htd-lw__panel">
        <div className="htd-lw__label">เลือกราคาหมุน</div>
        <div className="htd-lw__row">
          {modes.map((mode) => {
            const price =
              paymentType === 'cash'
                ? `${Number(mode.costCash).toLocaleString('th-TH')} ฿`
                : `${Number(mode.costPoints).toLocaleString('th-TH')} pts`
            return (
              <button
                key={mode.key}
                type="button"
                className={`htd-lw__chip${modeKey === mode.key ? ' is-active' : ''}`}
                disabled={spinning}
                onClick={() => setModeKey(mode.key)}
              >
                {mode.label}
                <span style={{ opacity: 0.85, fontWeight: 600 }}> · {price}</span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="htd-lw__panel">
        <div className="htd-lw__row" style={{ justifyContent: 'space-between' }}>
          <span className="htd-lw__badge">
            <i className="fas fa-ticket-alt" />
            ตั๋วฟรี {ticketCount} ใบ
          </span>
          {ticketCount > 0 ? (
            <label className="htd-lw__check">
              <input
                type="checkbox"
                checked={useTicket}
                disabled={spinning}
                onChange={(e) => setUseTicket(e.target.checked)}
              />
              ใช้ตั๋วเล่นฟรี
            </label>
          ) : (
            <span className="htd-lw__muted" style={{ margin: 0, padding: 0 }}>
              ยังไม่มีตั๋วที่ใช้ได้กับโหมดนี้
            </span>
          )}
        </div>
      </div>

      <div className="htd-lw__stage">
        <button
          type="button"
          className={`htd-lw__sound${soundEnabled ? ' is-on' : ''}`}
          onClick={toggleSound}
          aria-pressed={soundEnabled}
          aria-label={soundEnabled ? 'ปิดเสียงวงล้อ' : 'เปิดเสียงวงล้อ'}
        >
          <i className={`fas ${soundEnabled ? 'fa-volume-up' : 'fa-volume-mute'}`} />
          {soundEnabled ? 'เสียงเปิด' : 'เสียงปิด'}
        </button>

        <div className="htd-lw__wheel-wrap">
          <div className="htd-lw__pointer" aria-hidden />
          <div
            className={`htd-lw__wheel${spinning ? ' is-spinning' : ''}`}
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: `transform ${spinDurationMs}ms cubic-bezier(0.09, 0.65, 0.06, 1)`,
            }}
          >
            <WheelSvg segments={segments} winnerIndex={winnerIndex} />
          </div>
          <div className="htd-lw__hub" aria-hidden>
            SPIN
          </div>
        </div>

        <button
          type="button"
          className="htd-lw__spin-btn"
          disabled={spinning || !selectedMode || segments.length === 0}
          onClick={() => void onSpin()}
        >
          {spinning ? 'กำลังหมุน...' : `หมุน · ${costLabel}`}
        </button>

        <div className="htd-lw__result">{resultText}</div>
        {error ? <div className="htd-lw__error">{error}</div> : null}
      </div>
    </div>
  )
}

export default LuckyWheelGamePage
